import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navigate } from "react-router-dom";
import { ShieldCheck, Flag, Users, Note, Trash, Check } from "@phosphor-icons/react";
import { Navbar } from "../components/Navbar";
import { Avatar } from "../components/Avatar";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { timeAgo } from "../lib/helpers";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/admin/reports"), api.get("/admin/stats")]);
      setReports(r.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const removePost = async (report) => {
    try {
      await api.delete(`/posts/${report.post_id}`);
      toast.success("Post removed");
      load();
    } catch {
      toast.error("Could not remove post");
    }
  };

  const dismiss = async (report) => {
    try {
      await api.post(`/admin/reports/${report.id}/dismiss`);
      toast.success("Report dismissed");
      setReports((x) => x.filter((r) => r.id !== report.id));
    } catch {
      toast.error("Could not dismiss");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={26} weight="duotone" color="#8A9A86" />
          <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#8A9A86]">Moderation</span>
        </div>
        <h1 className="font-heading text-4xl font-semibold text-[#2C362B] mb-8">Keeping the space safe</h1>

        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-10">
            <Stat icon={<Flag size={22} weight="duotone" />} label="Open reports" value={stats.open_reports} testid="stat-reports" />
            <Stat icon={<Note size={22} weight="duotone" />} label="Total posts" value={stats.total_posts} testid="stat-posts" />
            <Stat icon={<Users size={22} weight="duotone" />} label="Members" value={stats.total_users} testid="stat-users" />
          </div>
        )}

        <h2 className="font-heading text-xl font-medium text-[#2C362B] mb-4">Reported posts ({reports.length})</h2>

        {loading ? (
          <p className="text-center text-[#8A9A86] py-12">Loading…</p>
        ) : reports.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-[2rem] border border-[#E6EBE4]/60">
            <ShieldCheck size={48} weight="duotone" color="#B3C0AE" className="mx-auto mb-3" />
            <p className="font-heading text-xl text-[#5E6C5C]">All clear</p>
            <p className="text-[#8A9A86] mt-1">No open reports right now.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="reports-list">
            {reports.map((rep) => (
              <motion.div
                key={rep.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[2rem] p-6 border border-[#E6EBE4]/60"
                data-testid="report-item"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-bold bg-[#FBEAE7] text-[#D97B6A] rounded-full px-3 py-1">{rep.reason}</span>
                  <span className="text-xs text-[#A8B0A3]">{timeAgo(rep.created_at)}</span>
                </div>
                {rep.post ? (
                  <div className="bg-[#FDFBF7] rounded-2xl p-4 border border-[#E6EBE4]/60">
                    <div className="flex items-center gap-2 mb-2">
                      <Avatar handle={rep.post.handle} size={28} />
                      <span className="text-sm font-semibold text-[#414C3F]">{rep.post.handle}</span>
                    </div>
                    <p className="text-[#515A4F] whitespace-pre-wrap break-words">{rep.post.content}</p>
                  </div>
                ) : (
                  <p className="text-sm text-[#A8B0A3] italic">This post has already been removed.</p>
                )}
                <div className="flex items-center gap-3 mt-4">
                  {rep.post && (
                    <button onClick={() => removePost(rep)} className="flex items-center gap-2 bg-[#D97B6A] text-white hover:bg-[#c96a59] rounded-full px-5 py-2.5 text-sm font-semibold transition-all" data-testid="remove-post-btn">
                      <Trash size={16} weight="bold" /> Remove post
                    </button>
                  )}
                  <button onClick={() => dismiss(rep)} className="flex items-center gap-2 bg-[#F4F1E8] text-[#515A4F] hover:bg-[#EAE6D9] rounded-full px-5 py-2.5 text-sm font-semibold transition-all" data-testid="dismiss-report-btn">
                    <Check size={16} weight="bold" /> Dismiss
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const Stat = ({ icon, label, value, testid }) => (
  <div className="bg-white rounded-[2rem] p-5 border border-[#E6EBE4]/60 text-center" data-testid={testid}>
    <div className="flex justify-center text-[#8A9A86] mb-2">{icon}</div>
    <p className="font-heading text-3xl font-semibold text-[#2C362B]">{value}</p>
    <p className="text-xs text-[#8A9A86] mt-1">{label}</p>
  </div>
);
